import { clamp } from "lodash-es";
import { GameEngine } from "~/engine/instance";
import { WormEntity } from "~/entities/worm";
import type { Vector2 } from "~/state/game-types";

export type WormSpawnConfig = {
  spawnBelowVoxelY: number;
  maxActiveWorms: number;
  spawnChancePerSecond: number; // probability each second when conditions met
};

export class WormSystem {
  private worms: WormEntity[] = [];
  private timeAccumulator = 0;
  private readonly cfg: WormSpawnConfig = {
    spawnBelowVoxelY: 50,
    maxActiveWorms: 1,
    spawnChancePerSecond: 0.3,
  };

  public update(deltaTime: number): void {
    // Update existing worms
    for (let i = this.worms.length - 1; i >= 0; i--) {
      this.worms[i]!.update(deltaTime);
    }

    // Spawning logic based on player depth
    this.timeAccumulator += deltaTime;
    if (this.timeAccumulator < 1.0) return;
    const seconds = Math.floor(this.timeAccumulator);
    this.timeAccumulator -= seconds;

    if (this.worms.length >= this.cfg.maxActiveWorms) return;

    const player = GameEngine.player;
    if (!player) return;

    const worldHeightInVoxels = GameEngine.voxelTerrain.worldHeightInVoxels;

    const playerVoxel = GameEngine.voxelTerrain.worldToVoxel(player.position);
    if (playerVoxel.y > worldHeightInVoxels - this.cfg.spawnBelowVoxelY) return;

    // Each elapsed second, attempt spawn with configured probability
    for (let s = 0; s < seconds; s++) {
      if (Math.random() < this.cfg.spawnChancePerSecond) {
        this.spawnNearPlayer();
        break;
      }
    }
  }

  private spawnNearPlayer(): void {
    const terrain = GameEngine.voxelTerrain;
    const player = GameEngine.player!;

    // Spawn within a ring around player, deeper than player by a little, random angle
    const radiusWorld = terrain.voxelSize * 40; // ~40 voxels away
    const angle = Math.random() * Math.PI * 2;
    const offset = {
      x: Math.cos(angle) * radiusWorld,
      y: Math.sin(angle) * radiusWorld,
    };

    const start: Vector2 = {
      x: player.position.x + offset.x,
      y: player.position.y + offset.y,
    };

    // Clamp within world vertical bounds
    start.y = clamp(start.y, 0, terrain.worldHeight - terrain.voxelSize);

    // Aim roughly toward the player with some variance
    const targetAngle = Math.atan2(
      player.position.y - start.y,
      player.position.x - start.x,
    );
    const initialAngle = targetAngle + (Math.random() * 2 - 1) * (Math.PI / 6);

    const worm = new WormEntity(start, initialAngle);
    this.worms.push(worm);
  }

  public dispose(): void {
    for (const w of this.worms) w.dispose();
    this.worms.length = 0;
  }
}
